import { useCallback } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import type { ValidationProblemDetails } from 'src/services/api/api-client';

const getStringMessages = (value: unknown): string[] => {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.filter((message): message is string => typeof message === 'string');
  return [];
};

const getRootMessages = (error: ValidationProblemDetails): string[] => {
  return [
    ...getStringMessages(error.detail),
    ...(Object.keys(error.errors).length ? [] : getStringMessages(error.details)),
  ];
};

export const useHookformValidationErrorHandler = <T extends FieldValues>(
  form: Pick<UseFormReturn<T>, 'control' | 'setError'>,
) => {
  const { control, setError } = form;

  return useCallback(
    (error: ValidationProblemDetails) => {
      const rootMessages = getRootMessages(error);

      Object.entries(error.errors).forEach(([key, messages]) => {
        const registeredFieldName = Array.from(control._names.mount).find(
          (fieldName) => fieldName.toLowerCase() === key.toLowerCase(),
        );

        if (registeredFieldName) {
          setError(registeredFieldName as never, { type: 'server', message: messages.join('\n') });
          return;
        }

        rootMessages.push(...messages);
      });

      if (rootMessages.length) {
        setError('root', { type: 'server', message: rootMessages.join('\n') });
      }
    },
    [control, setError],
  );
};
